import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { Payee } from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { AccountErrorCodes, AccountSuccessCodes } from '@app/translations';
import { FormID } from '@finxone-platform/shared/enums';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ACCOUNTTYPE,
  ActionButtonOutcomesType,
  AlertTypes,
  BaseWidgetProperties,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { finalize, take } from 'rxjs';
import {
  ClearFormDataActionWithId,
  UpdateFormDataActionWithId,
} from '../../../actions/form-submission.action';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import { UpdateNominatedAccountRequest } from '../../../services/savings-account-service/savings-account.type';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { handleRedirection } from '../cta-redirection.utils';

interface NominatedAccountFormValues {
  name: string;
  sortCode: string;
  accountNumber: string;
}

function getNominatedAccountFormValues(
  workflowFormsService: WorkflowFormsService,
  alertHandlerService: AlertHandlerService,
): NominatedAccountFormValues | null {
  const formValues =
    workflowFormsService.getAllFormValuesFlattened()[FormID.UPDATE_NOMINATED_BANK_ACCOUNT_FORM];

  const name = formValues?.['linkedAccountName'] as string;
  const sortCode = formValues?.['linkedAccountSort'] as string;
  const accountNumber = formValues?.['linkedAccountNumber'] as string;

  if (!name || !sortCode || !accountNumber) {
    alertHandlerService.showAlertFn(
      AlertTypes.ERROR,
      'Please provide account name, sort code, and account number.',
    );
    return null;
  }

  return { name, sortCode, accountNumber };
}

export function checkPayeeForNominatedAccount(
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const store = injector.get(Store);
  const accountService = injector.get(AccountService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const ctaAction = widgetProperties.buttonActionType as string;

  const formValues = getNominatedAccountFormValues(workflowFormsService, alertHandlerService);
  if (!formValues) return;

  const { name, sortCode, accountNumber } = formValues;

  const checkPayeeRequest: Payee = {
    name,
    accountNumber,
    sortCode,
    accountType: ACCOUNTTYPE.PERSONAL,
  };

  ctaButtonSignalService.setSignal({ [ctaAction]: false });

  accountService
    .checkPayee(checkPayeeRequest)
    .pipe(
      take(1),
      finalize(() => ctaButtonSignalService.setSignal({ [ctaAction]: true })),
    )
    .subscribe({
      next: (copResponse) => {
        store.dispatch(
          new UpdateFormDataActionWithId(
            { verificationToken: copResponse.verificationToken ?? '' },
            FormID.UPDATE_NOMINATED_BANK_ACCOUNT_FORM,
          ),
        );
        handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);
      },
      error: () => {
        handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
      },
    });
}

export function updateNominatedAccount(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const savingsAccountService = injector.get(SavingsAccountService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const ctaAction = widgetProperties.buttonActionType as string;

  const formValues = getNominatedAccountFormValues(workflowFormsService, alertHandlerService);
  if (!formValues) return;

  const { name, sortCode, accountNumber } = formValues;

  const formState = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormID.UPDATE_NOMINATED_BANK_ACCOUNT_FORM),
  );
  const verificationToken = formState?.formData?.['verificationToken'] as string;

  const nominatedAccountRequestPayload: UpdateNominatedAccountRequest = {
    accountName: name,
    accountIdentifier: {
      accountNumber,
      sortCode,
    },
    verificationToken: verificationToken ?? '',
  };

  ctaButtonSignalService.setSignal({ [ctaAction]: false });

  savingsAccountService
    .updateNominatedAccount(nominatedAccountRequestPayload)
    .pipe(
      take(1),
      finalize(() => ctaButtonSignalService.setSignal({ [ctaAction]: true })),
    )
    .subscribe({
      next: () => {
        store.dispatch(new ClearFormDataActionWithId(FormID.UPDATE_NOMINATED_BANK_ACCOUNT_FORM));
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          AccountSuccessCodes.NOMINATED_BANK_ACCOUNT_UPDATED,
        ]);
        handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);
      },
      error: () => {
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          AccountErrorCodes.NOMINATED_BANK_ACCOUNT_UPDATE_FAILED,
        ]);
        handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
      },
    });
}
